import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type SshKey } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardBody } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Plus, Trash2 } from 'lucide-react';
import { relativeTime } from '@/lib/utils';

export function SshKeysPage() {
  const [open, setOpen] = useState(false);
  const keys = useQuery({
    queryKey: ['ssh-keys'],
    queryFn: () => api.get<{ ssh_keys: SshKey[] }>('/api/ssh-keys'),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">SSH keys</h1>
          <p className="text-sm text-slate-500 mt-1">
            Deploy keys used to run lifecycle commands on orchestrator hosts.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="self-start sm:self-auto shrink-0">
          <Plus size={14} /> Add key
        </Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {keys.isLoading ? (
            <div className="p-5 text-sm text-slate-500">Loading…</div>
          ) : !keys.data?.ssh_keys.length ? (
            <div className="p-8 text-center text-sm text-slate-500">
              No keys stored. Add one before registering a server.
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="px-5 py-3">Label</th>
                  <th className="px-3 py-3">Fingerprint (SHA256)</th>
                  <th className="px-3 py-3">Created</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {keys.data.ssh_keys.map((k) => (
                  <KeyRow key={k.id} sshKey={k} />
                ))}
              </tbody>
            </table>
            </div>
          )}
        </CardBody>
      </Card>

      <AddKeyDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

function KeyRow({ sshKey }: { sshKey: SshKey }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const del = useMutation({
    mutationFn: () => api.del(`/api/ssh-keys/${sshKey.id}`),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['ssh-keys'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50 last:border-0">
        <td className="px-5 py-3 text-slate-900">{sshKey.label}</td>
        <td className="px-3 py-3 font-mono text-xs text-slate-700 break-all">
          {sshKey.public_fingerprint}
        </td>
        <td className="px-3 py-3 text-xs text-slate-500">{relativeTime(sshKey.created_at)}</td>
        <td className="px-5 py-3 text-right">
          <Button
            variant="ghost"
            size="sm"
            disabled={del.isPending}
            onClick={() => {
              if (confirm(`Delete key "${sshKey.label}"?`)) {
                setError(null);
                del.mutate();
              }
            }}
          >
            <Trash2 size={14} />
          </Button>
        </td>
      </tr>
      {error && (
        <tr className="border-b border-slate-100 last:border-0">
          <td colSpan={4} className="px-5 pb-3 pt-0">
            <div className="flex items-start justify-between gap-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700 shrink-0"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function AddKeyDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ label: '', private_key: '', public_key: '' });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.post('/api/ssh-keys', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ssh-keys'] });
      onOpenChange(false);
      setForm({ label: '', private_key: '', public_key: '' });
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add SSH key"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add key'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>}
        <div>
          <Label htmlFor="label">Label</Label>
          <Input
            id="label"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="cp-deploy"
          />
        </div>
        <div>
          <Label htmlFor="public_key">Public key</Label>
          <Textarea
            id="public_key"
            rows={3}
            value={form.public_key}
            onChange={(e) => setForm({ ...form, public_key: e.target.value })}
            placeholder="ssh-ed25519 AAAA… deploy@cp"
            className="font-mono text-xs"
          />
          <p className="text-xs text-slate-500 mt-1">
            Used only to compute the fingerprint shown in the table — install it on the host's
            <code className="mx-1 font-mono">~/.ssh/authorized_keys</code> separately.
          </p>
        </div>
        <div>
          <Label htmlFor="private_key">Private key</Label>
          <Textarea
            id="private_key"
            rows={6}
            value={form.private_key}
            onChange={(e) => setForm({ ...form, private_key: e.target.value })}
            placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
            className="font-mono text-xs"
          />
          <p className="text-xs text-slate-500 mt-1">
            Stored encrypted with the master key. The control plane never returns the bytes again
            after saving.
          </p>
        </div>
      </div>
    </Dialog>
  );
}
